import { Popover } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FilterPill } from './ui';

interface SelectPillProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  selected: string;
  onChange: (value: string) => void;
}

export function SelectPill({ label, value, options, selected, onChange }: SelectPillProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      getPopupContainer={() => document.body}
      content={
        <div className="p-1 flex flex-col min-w-[160px]">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`text-left px-2 py-1.5 text-[12.5px] rounded hover:bg-bg-elevated ${
                selected === opt.value ? 'text-accent font-semibold' : 'text-text-2'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      }
    >
      <span>
        <FilterPill label={label} value={value} active={!!selected} />
      </span>
    </Popover>
  );
}

interface TextPillProps {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}

export function TextPill({ label, value, placeholder, onChange }: TextPillProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const reposition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    reposition();
    function handleClick(e: MouseEvent) {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        dropRef.current?.contains(e.target as Node)
      )
        return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, reposition]);

  return (
    <>
      <span ref={triggerRef} onClick={() => setOpen((o) => !o)}>
        <FilterPill label={label} value={value || 'Any'} active={!!value} />
      </span>
      {open &&
        createPortal(
          <div
            ref={dropRef}
            style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 1050 }}
            className="bg-panel border border-border-strong rounded-lg shadow-lg p-2 w-[220px]"
          >
            <input
              type="text"
              value={draft}
              placeholder={placeholder}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onChange(draft);
                  setOpen(false);
                }
                if (e.key === 'Escape') setOpen(false);
              }}
              className="w-full max-w-full box-border px-2 py-1.5 bg-panel border border-border-strong rounded-md text-[12.5px] text-text outline-none focus:border-accent"
            />
            <div className="flex justify-end gap-1.5 mt-2">
              <button
                type="button"
                onClick={() => {
                  setDraft('');
                  onChange('');
                  setOpen(false);
                }}
                className="text-[11px] text-text-3 hover:text-text px-2 py-1"
              >
                clear
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange(draft);
                  setOpen(false);
                }}
                className="text-[11px] text-accent font-semibold hover:opacity-80 px-2 py-1"
              >
                apply
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
